import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  Target,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActionPlan() {
  const { user, loading: authLoading } = useAuth();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTask, setExpandedTask] = useState(null);
  const [updatingTask, setUpdatingTask] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/app/login');
      return;
    }
    if (user) {
      fetchPlan();
    }
  }, [user, authLoading, navigate]);

  const fetchPlan = async () => {
    try {
      const response = await fetch(`${API_URL}/api/app/student/plan`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPlanData(data);
      }
    } catch (error) {
      console.error('Failed to fetch plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    setUpdatingTask(taskId);
    try {
      const response = await fetch(`${API_URL}/api/app/student/plan/task/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        await fetchPlan(); // Refresh the plan
      }
    } catch (error) {
      console.error('Failed to update task:', error);
    } finally {
      setUpdatingTask(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <Circle className="w-5 h-5 text-slate-300" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'career_direction': return 'bg-teal-100 text-teal-700';
      case 'profile_strength': return 'bg-blue-100 text-blue-700';
      case 'interview_readiness': return 'bg-amber-100 text-amber-700';
      case 'job_search': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const categoryNames = {
    career_direction: 'Career Direction',
    profile_strength: 'Profile Strength',
    interview_readiness: 'Interview Readiness',
    job_search: 'Job Search',
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const plan = planData?.plan;
  const role = planData?.role;

  // No plan state
  if (!plan) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/app/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back</span>
            </Link>
            <div className="bg-white border border-slate-200 px-2 py-1 rounded-lg">
              <img src="/logo-elevate-careers.webp" alt="Elevate Careers" className="h-6 w-auto" />
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <Target className="w-12 h-12 text-teal-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">No Action Plan Yet</h2>
            <p className="text-slate-600 mb-6">
              Select a target role to get your personalized 7-day action plan.
            </p>
            <Link to="/app/role-select">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                Choose Your Role
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const tasks = plan.tasks || [];
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const completionPercent = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/app/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back</span>
          </Link>
          <div className="bg-white border border-slate-200 px-2 py-1 rounded-lg">
            <img src="/logo-elevate-careers.webp" alt="Elevate Careers" className="h-6 w-auto" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Title & Progress */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Your 7-Day Plan</h1>
              {role && (
                <p className="text-slate-600 text-sm mt-1">
                  Path: {role.name}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-teal-600">{completionPercent}%</p>
              <p className="text-xs text-slate-500">Complete</p>
            </div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-teal-600 rounded-full transition-all duration-500"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          <p className="text-sm text-slate-500 mt-2">
            {completedTasks} of {tasks.length} tasks completed
          </p>
        </div>

        {/* Tasks List */}
        <div className="space-y-3">
          {tasks.map((task, index) => (
            <div 
              key={task.id} 
              className={`bg-white rounded-xl border overflow-hidden transition-all ${
                task.status === 'completed' 
                  ? 'border-emerald-200 bg-emerald-50/30' 
                  : 'border-slate-200'
              }`}
            >
              {/* Task Header */}
              <button
                onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                className="w-full p-4 flex items-start gap-3 text-left"
                data-testid={`task-${index}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getStatusIcon(task.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-slate-400">Day {task.day}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getCategoryColor(task.category)}`}>
                      {categoryNames[task.category]}
                    </span>
                  </div>
                  <h3 className={`font-medium ${
                    task.status === 'completed' ? 'text-slate-500 line-through' : 'text-slate-900'
                  }`}>
                    {task.title}
                  </h3>
                </div>
                <div className="flex-shrink-0">
                  {expandedTask === task.id ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>

              {/* Task Details (Expanded) */}
              {expandedTask === task.id && (
                <div className="px-4 pb-4 border-t border-slate-100">
                  <div className="pt-4">
                    <p className="text-sm text-slate-600 mb-4">
                      {task.description}
                    </p>

                    {/* Status Actions */}
                    <div className="flex flex-wrap gap-2">
                      {task.status !== 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateTaskStatus(task.id, 'pending')}
                          disabled={updatingTask === task.id}
                          className="text-xs"
                        >
                          {updatingTask === task.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            'Reset'
                          )}
                        </Button>
                      )}
                      {task.status !== 'in_progress' && task.status !== 'completed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateTaskStatus(task.id, 'in_progress')}
                          disabled={updatingTask === task.id}
                          className="text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                        >
                          {updatingTask === task.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Clock className="w-3 h-3 mr-1" />
                              Start
                            </>
                          )}
                        </Button>
                      )}
                      {task.status !== 'completed' && (
                        <Button
                          size="sm"
                          onClick={() => updateTaskStatus(task.id, 'completed')}
                          disabled={updatingTask === task.id}
                          className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {updatingTask === task.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Mark Complete
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Feedback if exists */}
                    {task.feedback && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-xs font-medium text-blue-700 mb-1">Navigator Feedback</p>
                        <p className="text-sm text-blue-800">{task.feedback}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
